import { Directive, output, signal } from '@angular/core';

/**
 * Turns its host into a file drop target: emits the dropped files and sets the
 * `fileover` class while files are dragged over it. A depth counter keeps the
 * class steady while the pointer crosses child elements (each fires its own
 * enter/leave), and drags that carry no files — selected text, links — are
 * left alone.
 */
@Directive({
  selector: '[appDnd]',
  standalone: true,
  host: {
    '[class.fileover]': 'fileOver()',
    '(dragenter)': 'onDragEnter($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(drop)': 'onDrop($event)',
  },
})
export class DndDirective {
  readonly fileDropped = output<FileList>();

  protected readonly fileOver = signal(false);
  private _depth = 0;

  protected onDragEnter(evt: DragEvent) {
    if (!carriesFiles(evt)) return;
    evt.preventDefault();
    this._depth++;
    this.fileOver.set(true);
  }

  protected onDragOver(evt: DragEvent) {
    if (!carriesFiles(evt)) return;
    // Required for the element to accept the drop.
    evt.preventDefault();
    evt.stopPropagation();
    if (evt.dataTransfer) evt.dataTransfer.dropEffect = 'copy';
  }

  protected onDragLeave(evt: DragEvent) {
    if (!carriesFiles(evt)) return;
    this._depth = Math.max(0, this._depth - 1);
    if (this._depth === 0) this.fileOver.set(false);
  }

  protected onDrop(evt: DragEvent) {
    if (!carriesFiles(evt)) return;
    evt.preventDefault();
    evt.stopPropagation();
    this._depth = 0;
    this.fileOver.set(false);
    const files = evt.dataTransfer?.files;
    if (files && files.length > 0) this.fileDropped.emit(files);
  }
}

function carriesFiles(evt: DragEvent): boolean {
  return Array.from(evt.dataTransfer?.types ?? []).includes('Files');
}
